<script setup>
// Full-page details body shown when the "Check-in details" row is opened
// (the sheet header switches to back mode).
// Port of `DetailsView` + `DetailSection` + `FrequencyLine` in the source
// `_shared/CheckInSheet.tsx`.
import { computed } from 'vue'
import { Info, ListChecks, CalendarOff, RefreshCw, Clock } from '@lucide/vue'
import { perLabel } from './checkin'

const props = defineProps({
  details: { type: Object, required: true },
})

const frequency = computed(() => props.details.frequency || null)
const usedCount = computed(() => frequency.value?.usedCount ?? 0)
const remaining = computed(() => (frequency.value ? Math.max(0, frequency.value.cap - usedCount.value) : 0))
</script>

<template>
  <div class="cs-details-view">
    <div class="cs-details-view__title">
      <Info class="cs-details-view__accent-icon" :size="14" />
      <span class="cs-details-view__title-label">Check-in details</span>
    </div>

    <div class="cs-details-view__sections">
      <p v-if="details.description" class="cs-details-view__description">{{ details.description }}</p>

      <div v-if="details.howToUse?.length" class="cs-section">
        <div class="cs-section__label">
          <ListChecks class="cs-details-view__accent-icon" :size="14" />
          How to use
        </div>
        <div class="cs-section__steps">
          <p v-for="(step, i) in details.howToUse" :key="i" class="cs-section__step">{{ step }}</p>
        </div>
      </div>

      <div v-if="frequency" class="cs-section">
        <div class="cs-section__label">
          <RefreshCw class="cs-details-view__accent-icon" :size="14" />
          Frequency
        </div>
        <div class="cs-frequency">
          <div>
            Up to <span class="cs-frequency__strong">{{ frequency.cap }} check-in{{ frequency.cap === 1 ? '' : 's' }}</span>
            {{ perLabel(frequency.per) }}
            <span class="cs-frequency__muted">· {{ remaining }} left ({{ usedCount }}/{{ frequency.cap }} used)</span>
          </div>
          <div v-if="remaining === 0 && frequency.nextAvailableLabel" class="cs-frequency__next">
            <Clock :size="12" /> Next check-in {{ frequency.nextAvailableLabel }}
          </div>
        </div>
      </div>

      <div v-if="details.restrictions?.length" class="cs-section">
        <div class="cs-section__label">
          <Info class="cs-details-view__accent-icon" :size="14" />
          Restrictions
        </div>
        <ul class="cs-restrictions">
          <li v-for="(r, i) in details.restrictions" :key="i" class="cs-restrictions__item">
            <span class="cs-restrictions__dot" />
            <span>{{ r }}</span>
          </li>
        </ul>
      </div>

      <div v-if="details.blackoutDates?.length" class="cs-section">
        <div class="cs-section__label">
          <CalendarOff class="cs-details-view__accent-icon" :size="14" />
          Blackout dates
        </div>
        <div class="cs-blackout-chips">
          <span v-for="d in details.blackoutDates" :key="d" class="cs-blackout-chips__chip">{{ d }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `space-y-3 pt-1 pb-3`
.cs-details-view {
  padding-top: 4px; // pt-1
  padding-bottom: 12px; // pb-3

  > * + * {
    margin-top: 12px; // space-y-3
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px; // gap-2
  }

  &__accent-icon {
    color: var(--fr-accent, var(--fr-primary, #FF9900));
    flex-shrink: 0;
  }

  // Source: `font-display text-[11px] font-bold uppercase tracking-wider`
  &__title-label {
    @include cs-overline(11px);
  }

  &__sections > * + * {
    margin-top: 12px; // space-y-3
  }

  // Source: `text-[12px] leading-snug ${theme.muted}`
  &__description {
    margin: 0;
    font-size: 12px;
    line-height: 1.375;
    color: var(--fr-muted, #6b7280);
  }
}

.cs-section {
  // Source DetailSection label: `mb-1 flex items-center gap-1.5 text-[10px]
  //                              font-bold uppercase tracking-wider opacity-70`
  &__label {
    margin-bottom: 4px; // mb-1
    display: flex;
    align-items: center;
    gap: 6px; // gap-1.5
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.7;
  }

  // Source: `space-y-1.5 text-[12px] leading-snug`
  &__steps {
    font-size: 12px;
    line-height: 1.375;

    > * + * {
      margin-top: 6px; // space-y-1.5
    }
  }

  &__step {
    margin: 0;
    white-space: pre-line;
  }
}

// Source FrequencyLine: `space-y-0.5 text-[11px] leading-snug`
.cs-frequency {
  font-size: 11px;
  line-height: 1.375;

  > * + * {
    margin-top: 2px; // space-y-0.5
  }

  &__strong {
    font-weight: 600; // font-semibold
  }

  &__muted {
    margin-left: 4px; // ml-1
    color: var(--fr-muted, #6b7280);
  }

  &__next {
    display: inline-flex;
    align-items: center;
    gap: 4px; // gap-1
    color: var(--fr-muted, #6b7280);
  }
}

// Source: `<ul className="space-y-0.5">` of flex rows
.cs-restrictions {
  margin: 0;
  padding: 0;
  list-style: none;

  > * + * {
    margin-top: 2px; // space-y-0.5
  }

  // Source: `flex items-start gap-2 text-[12px] leading-snug`
  &__item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    line-height: 1.375;
  }

  // Source: `mt-1.5 h-1 w-1 shrink-0 rounded-full bg-current opacity-50`
  &__dot {
    margin-top: 6px; // mt-1.5
    height: 4px;
    width: 4px;
    flex-shrink: 0;
    border-radius: 9999px;
    background: currentColor;
    opacity: 0.5;
  }
}

// Source: `flex flex-wrap gap-1.5` of pills
.cs-blackout-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px; // gap-1.5

  // Source: `rounded-full bg-current/[0.06] px-2 py-0.5 text-[11px]
  //          font-medium ring-1 ring-current/10`
  &__chip {
    border-radius: 9999px;
    background: cs-current(6%);
    padding: 2px 8px; // px-2 py-0.5
    font-size: 11px;
    font-weight: 500;
    box-shadow: 0 0 0 1px cs-current(10%);
  }
}
</style>
